import { useCallback } from 'react';
import { getPipeexSettings } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';
import { useSampledParameterNamesStore } from '../../../shared/stores/useSampledParameterNamesStore';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { assessMappingFamilyRequirementsGrpc } from '../../../shared/api/grpc/familyRequirementAssessmentClient';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import type { BoltConfiguration } from '../../../shared/types';
import {
  collectMappedParameterReferencesFromSettingsDto,
  diffMappedParametersAgainstCatalog,
  scoreMappingProfilesAgainstCatalog,
} from '../lib/mappingProjectReadiness';
import {
  buildSelectedMappingParameterScopeRequirements,
  type MappingParameterAssessment,
} from '../lib/mappingReadinessFamilyExclusions';
import { buildMappingFamilyRequirementAssessmentInputs } from '../lib/mappingReadinessSnapshotRequest';
import {
  getSettingsDtoAdditionalCategoriesConfiguration,
  getSettingsDtoBoltConfigurationInput,
  getSettingsDtoConnectorConfiguration,
  getSettingsDtoMenuConfiguration,
} from '../lib/settingsDtoFamilyDestination';
import { warnMappingFlow } from '../lib/mappingTrace';
import { parseSettingsType } from '../lib/profile';
import { formatMissingParameterPreview } from '../lib/formatMissingParameterPreview';
import { toMappingParameterAssessments } from './useMappingFamilyRequirementBackendAssessment';
import { useConfirmationDialog } from '../../../shared/ui/dialogs/useConfirmationDialog';
import type { useProjectProfileGate } from './useProjectProfileGate';
import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';

interface UseProfileLoadConfirmationArgs {
  profileProjectMatches: ReturnType<typeof useProjectProfileGate>['profileProjectMatches'];
  activeDocumentInfo: Pick<ActiveDocumentInfo, 'projectKey' | 'modelVersion'>;
  lastAppliedProfileKey: string;
  selectedProfileKey: string;
  onProfileLoadConfirmed?: (profileKey: string) => void;
}

/**
 * "Load profile?" destructive-confirmation flow: builds the missing-parameter
 * preview for the target profile (cached match first, the same scope-aware
 * scoring path on demand as fallback) and asks the user before the load
 * replaces current settings.
 */
export const useProfileLoadConfirmation = ({
  profileProjectMatches,
  activeDocumentInfo,
  lastAppliedProfileKey,
  selectedProfileKey,
  onProfileLoadConfirmed,
}: UseProfileLoadConfirmationArgs) => {
  const {
    requestConfirmation: requestProfileLoadConfirmation,
    confirmationDialog: profileLoadConfirmationDialog,
  } = useConfirmationDialog();

  const buildProfileLoadConfirmationPreview = useCallback(
    async (profileKey: string): Promise<{ missingCount: number; missingPreview: string | null }> => {
      const cachedMatch = profileProjectMatches.matches.find(
        (item) => item.profileKey === profileKey
      );
      if (cachedMatch) {
        return {
          missingCount: cachedMatch.missingCount,
          missingPreview: formatMissingParameterPreview(cachedMatch.missingParameterNames),
        };
      }

      try {
        const [name, settingsTypeRaw] = profileKey.split('::');
        if (!name) {
          return { missingCount: 0, missingPreview: null };
        }

        const settingsType = parseSettingsType(settingsTypeRaw);
        const dto = await getPipeexSettings(name, settingsType);
        const references = collectMappedParameterReferencesFromSettingsDto(dto);
        const parameterStore = useRevitParametersStore.getState();
        const normalizedProjectKey = normalizeMappingProjectKey(activeDocumentInfo.projectKey);
        const projectCatalogMatches =
          parameterStore.projectKey === normalizedProjectKey &&
          parameterStore.modelVersion === activeDocumentInfo.modelVersion;
        const projectParameterNames = projectCatalogMatches
          ? parameterStore.parameters.flatMap((parameter) => [parameter.name, parameter.label])
          : [];
        let sampledParameterNames: string[] = [];
        if (isGrpcRuntimeAvailable()) {
          try {
            sampledParameterNames = await useSampledParameterNamesStore.getState().fetchNames({
              projectKey: normalizedProjectKey,
              modelVersion: activeDocumentInfo.modelVersion,
            });
          } catch {
            // Ignore sampled catalog failures; project parameters still give a useful preview.
          }
        }

        const menuConfig = getSettingsDtoMenuConfiguration(dto);
        const connectorConfig = getSettingsDtoConnectorConfiguration(dto);
        const boltConfig = getSettingsDtoBoltConfigurationInput(dto);
        const additionalCategoriesConfig = getSettingsDtoAdditionalCategoriesConfiguration(dto);
        if (!menuConfig || !connectorConfig || !boltConfig || !additionalCategoriesConfig) {
          // Profiles saved before the wizard snapshot existed carry no
          // storage-mode metadata and keep the legacy project-level behavior,
          // so the name-only catalog diff is the correct verdict for them:
          // no family-destination parameters can exist without the snapshot.
          const diff = diffMappedParametersAgainstCatalog(references, {
            projectParameterNames,
            sampledParameterNames,
          });
          const missingNames = diff.missing.map((reference) => reference.name);
          return {
            missingCount: missingNames.length,
            missingPreview: formatMissingParameterPreview(missingNames),
          };
        }

        const requirements = buildSelectedMappingParameterScopeRequirements(
          {
            menuConfig,
            connectorConfig,
            boltConfig: boltConfig as unknown as BoltConfiguration,
            additionalCategoriesConfig,
          },
          references
        );
        const familyRequirements = requirements.filter(
          (requirement) => requirement.target.kind !== 'projectCategories'
        );
        let familyScopeAssessments: MappingParameterAssessment[] = [];
        if (familyRequirements.length > 0) {
          const result = await assessMappingFamilyRequirementsGrpc(
            buildMappingFamilyRequirementAssessmentInputs(familyRequirements)
          );
          if (!result.success) {
            // The primary route blocks on this error instead of scoring; the
            // fallback must not replace that blocked verdict with a name-only
            // missing preview that ignores the family storage mode.
            warnMappingFlow('profile load confirmation family assessment failed', {
              profileKey,
              error: result.error || 'Mapping family requirement assessment failed',
            });
            return { missingCount: 0, missingPreview: null };
          }
          familyScopeAssessments = toMappingParameterAssessments(
            result.assessments ?? [],
            familyRequirements
          );
        }

        const match = scoreMappingProfilesAgainstCatalog(
          [{
            profile: { name, settingsType, createdOn: '', changedOn: '' },
            dto,
            scopeRequirements: requirements,
            familyScopeAssessments,
          }],
          {
            projectParameterNames,
            sampledParameterNames,
          },
          projectCatalogMatches ? parameterStore.parameters : []
        )[0];
        const missingNames = match?.missingParameterNames ?? [];
        return {
          missingCount: missingNames.length,
          missingPreview: formatMissingParameterPreview(missingNames),
        };
      } catch {
        return { missingCount: 0, missingPreview: null };
      }
    },
    [
      activeDocumentInfo.modelVersion,
      activeDocumentInfo.projectKey,
      profileProjectMatches.matches,
    ]
  );

  const confirmLoadSelectedProfile = useCallback(async (profileKeyOverride?: string): Promise<boolean> => {
    const profileKey = profileKeyOverride?.trim() || selectedProfileKey;
    if (!profileKey) {
      return false;
    }

    const [profileName] = profileKey.split('::');
    const preview = await buildProfileLoadConfirmationPreview(profileKey);
    const { missingCount, missingPreview } = preview;
    const reloadingCurrentProfile = profileKey === lastAppliedProfileKey;

    let message: string;
    if (missingCount > 0 && missingPreview) {
      message = `The active Revit project is missing ${missingCount} PipeEX setup parameter${missingCount === 1 ? '' : 's'} required by “${profileName}”: ${missingPreview}. Applying this profile adds those parameters to Revit and replaces the current Mapping, Quick Fill, PCF Validation, and PCF export settings.`;
    } else {
      message = `Applying “${profileName}” replaces the current Mapping, Quick Fill, PCF Validation, and PCF export settings. PipeEX will also add any profile parameters that are missing from the active Revit project.`;
    }

    // Unsaved edits are not mentioned here: the navigation guard has already
    // offered Save / Discard / Stay before this consent is ever asked for.
    const confirmed = await requestProfileLoadConfirmation({
      title: reloadingCurrentProfile ? 'Reapply this mapping profile?' : 'Replace current settings with this profile?',
      message: `${message} Continue only if this is the intended profile for the active Revit project.`,
      confirmLabel: reloadingCurrentProfile ? 'Reapply profile' : 'Apply profile',
      cancelLabel: 'Keep current settings',
      destructive: true,
    });

    // This dialog explicitly tells the user that missing setup parameters will
    // be added. Carry that consent into the load instead of asking again after
    // the profile has already replaced the workspace settings.
    if (confirmed) {
      onProfileLoadConfirmed?.(profileKey);
    }

    return confirmed;
  }, [
    buildProfileLoadConfirmationPreview,
    lastAppliedProfileKey,
    onProfileLoadConfirmed,
    requestProfileLoadConfirmation,
    selectedProfileKey,
  ]);

  return {
    confirmLoadSelectedProfile,
    profileLoadConfirmationDialog,
  };
};
